import { stringEquals } from "./strings";

/**
 * @param {any} x
 * @returns {x is object}
 */
export function isObject(x) {
	return typeof(x) === "object";
}

/**
 * @param {any} x
 * @returns {x is string}
 */
export function isString(x) {
	return typeof(x) === "string";
}

/**
 * @param {any} x
 * @returns {x is boolean}
 */
export function isBoolean(x) {
	return typeof(x) === "boolean";
}

/**
 * @param {any} x
 * @returns {x is number}
 */
export function isNumber(x) {
	return typeof(x) === "number";
}

/**
 * 
 * @param {any} x 
 * @returns {x is Function}
 */
export function isFunction(x) {
	return typeof(x) === "function";
}

/**
 * @template T
 * @param {any} x
 * @param {import("./defs").TypeHint<T>} typeHint
 * @returns {x is import("./defs").Predicate<T>}
 */
export function isLikelyPredicate(x, typeHint) {
	return typeof(x) === "function" && x.length === 1;
}

/**
 * @template T
 * @param {any} x
 * @param {import("./defs").TypeHint<T>} typeHint
 * @returns {x is import("./defs").FactoryZero<T>}
 */
export function isLikelyFactoryZero(x, typeHint) {
	return typeof(x) === "function" && x.length === 0;
}

/**
 * @param {any} n
 * @returns {n is Node}
 */
export function isNode(n) {
	return n instanceof Node;
}

/**
 * Same as `isNode` but also checks that it's a text node rather than an explicit tag.
 * @param {any} n 
 * @returns {n is Node}
 */
export function isTextNode(n) {
	return isNode(n) && n.nodeName === "#text";
}

/**
 * @param {Node} n
 * @returns {n is Element}
 */
export function isElement(n) {
	return n instanceof Element;
}

/**
 * @param {Node} n
 * @returns {n is HTMLElement}
 */
export function isHtmlElement(n) {
	return n instanceof HTMLElement;
}

/**
 * @param {Node} n
 * @returns {n is SVGElement}
 */
export function isSvgElement(n) {
	return n instanceof SVGElement;
}

/**
 * @param {Node} n
 * @returns {n is import("./defs").BasicElement}
 */
export function isBasicElement(n) {
	return isHtmlElement(n) || isSvgElement(n);
}

/**
 * @param {Node} n
 * @returns {n is HTMLFieldSetElement}
 */
export function isFieldSetElement(n) {
	return n instanceof HTMLFieldSetElement;
}

/**
 * @param {Node} n
 * @returns {n is HTMLInputElement}
 */
export function isInputElement(n) {
	return n instanceof HTMLInputElement;
}

/**
 * @param {Node} n
 * @returns {n is HTMLTextAreaElement}
 */
export function isTextAreaElement(n) {
	return n instanceof HTMLTextAreaElement;
}

/**
 * @param {Node} n
 * @returns {n is HTMLSelectElement}
 */
export function isSelectElement(n) {
	return n instanceof HTMLSelectElement;
}

/**
 * @param {Node} n
 * @returns {n is HTMLLabelElement}
 */
export function isLabelElement(n) {
	return n instanceof HTMLLabelElement;
}

/**
 * @param {Node} n
 * @returns {n is HTMLFormElement}
 */
export function isFormElement(n) {
	return n instanceof HTMLFormElement;
}

/**
 * @param {Node} n
 * @returns {n is import("./defs").FormControl}
 */
export function isFormControl(n) {
	return n instanceof HTMLElement;
}

/**
 * @param {Element} el
 * @returns {el is HTMLElement}
 */
export function isDisabledElement(el) {
	return el.getAttribute("aria-disabled") === "true";
}

/**
 * @param {Element} el
 * @returns {el is HTMLElement}
 */
export function isHiddenElement(el) {
	return el.getAttribute("hidden") === "true";
}

/**
 * @param {Element} el
 * @returns {el is HTMLElement}
 */
export function isFocusableElement(el) {
	return !isDisabledElement(el) && !isHiddenElement(el);
}

/**
 * @template {Node} T
 * @param {T} a
 * @param {Node} b
 * @returns {b is T}
 */
export function isSameNodeType(a, b) {
	return a && b && stringEquals(a.nodeName, (/** @type {Node} */ (b)).nodeName);
}

/**
 * @template {Node} T
 * @param {T} a
 * @returns {(b: Node) => b is T}
 */
export function isSameNodeTypeFuncFactory(a) {
	return /** @type {(b: Node) => b is T} */ (b) => isSameNodeType(a, b);
}

/**
 * @template T
 * @param {import("./defs").MaybeFactory<T>} x 
 * @param {(x: any) => x is T} isX
 * @returns {T?}
 */
export function resolveIfFactory(x, isX) {
	return isX(x) ? x : typeof(x) === "function" ? (!x ? null : x()) : x;
}

/**
 * @template T
 * @param {import("./defs").MaybeFactory<T>} x 
 * @param {(x: any) => x is T} isX
 * @returns {Partial<T>}
 */
export const resolveIfFactoryPartial = resolveIfFactory;

/**
 * @template {object} T
 * @param {import("./defs").MaybeFactory<T>} x 
 * @returns {Partial<T>?}
 */
export function resolveOptions(x) {
	return resolveIfFactoryPartial(x, isObject);
}

/**
 * @template {object} T
 * @param {string|undefined} value 
 * @param {T} enumObject 
 * @param {import("./defs").valueof<T>} fallback 
 * @returns {import("./defs").valueof<T>}
 */
export function toEnum(value, enumObject, fallback) {
	if (value == null || !Object.keys(enumObject).includes(value)) {
		return fallback;
	}
	return /** @type {import("./defs").valueof<T>} */ (value);
}

/**
 * @param {Node} node 
 * @returns {node is import("../util/defs").ValidatableControl}
 */
export function isValidatableControl(node) {
	return node instanceof HTMLElement &&
	// @ts-ignore
	typeof(node.checkValidity) === "function" &&
	// @ts-ignore
	typeof(node.reportValidity) === "function"
}